import React, { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
    Avatar,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Popover,
    SvgIcon,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { MessageCircle as MessageIcon } from 'react-feather';
import moment from 'moment';
import BellIcon from '@material-ui/icons/Notifications';
import useAuth from 'src/hooks/useAuth';
import useNotification from 'src/hooks/useNotification';
import { useWebPush } from 'src/hooks/useWebPush';
import { useTranslation } from 'react-i18next';
import { channel } from 'src/service/pusher';
import { useSnackbar } from 'notistack';
import { BOTH, NEW, PREOWNED } from 'src/constants/CARTYPE';
import { DIGITAL_AREA } from 'src/constants/user';
import { webPushNotification } from 'src/modules/notifications/helpers/webPushNotification';
import { obtainUrlByNotificationType } from 'src/modules/notifications/helpers/obtainUrlByNotificationType';
import { authorize } from 'src/utils/auth';
import { ADMIN_LEVEL_ACCESS, USER_LEVEL_ACCESS } from 'src/components/access';

const useStyles = makeStyles((theme) => ({
    popover: {
        width: 320,
    },
    icon: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
    },
}));

const Notifications = ({ ...rest }) => {
    const classes = useStyles();
    const ref = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const { t } = useTranslation();
    const history = useHistory();
    const { user } = useAuth();
    const {
        updateNotification,
        getNotificationsByUser,
        notifications,
        updateList,
    } = useNotification();
    const { enqueueSnackbar } = useSnackbar();
    const { subscribe, isSupported, permission } = useWebPush(user?._id);

    const isSupportedRef = useRef(isSupported);
    const permissionRef = useRef(permission);

    useEffect(() => {
        isSupportedRef.current = isSupported;
        permissionRef.current = permission;
    }, [isSupported, permission]);

    const handleMarkAllAsRead = () => {
        if (notifications && notifications.length > 0)
            notifications.map((notification) =>
                updateNotification(notification._id)
            );
    };

    useEffect(() => {
        if (user?._id) getNotificationsByUser(user._id, false);

        if (user?._id && isSupported && permission === 'default') {
            subscribe().catch(() => {
                enqueueSnackbar(t('Errors.NoWebPushSupport'), {
                    variant: 'warning',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                });
            });
        }
    }, [user, isSupported, permission, subscribe]);

    useEffect(() => {
        if (user?.accessLevel) {
            const handleNewNotification = async (notification) => {
                updateList(notification);
                webPushNotification({
                    isSupported: isSupportedRef.current,
                    permission: permissionRef.current,
                    notification,
                    translate: t,
                });
                enqueueSnackbar(t(notification.message), {
                    variant: 'info',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                    onClick: () => {
                        history.push(`/management/leads/${notification.lead}`);
                    },
                });
                try {
                    const audio = new Audio('/static/notification.mp3');
                    await audio.play();
                } catch (err) {
                    enqueueSnackbar(t('Errors.NoAudio'), {
                        variant: 'warning',
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        },
                    });
                }
            };

            const bindNewNotification = (storeId, areaType) => {
                channel.unbind(`notification_by_store_${storeId}_${areaType}`);
                channel.bind(
                    `notification_by_store_${storeId}_${areaType}`,
                    (notification) => {
                        handleNewNotification(notification);
                    }
                );
            };

            const bindNewNotificationUser = (userId) => {
                channel.bind(
                    `notification_by_agent_${userId}`,
                    (notification) => {
                        handleNewNotification(notification);
                    }
                );
            };

            const { areas, stores, carType } = user;

            if (authorize(user?.accessLevel, ADMIN_LEVEL_ACCESS)) {
                const storesUser = stores || [];
                storesUser.forEach((store) => {
                    areas.forEach((area) => {
                        if (area === DIGITAL_AREA) {
                            if (carType === NEW || carType === BOTH) {
                                bindNewNotification(store._id, NEW);
                            }
                            if (carType === PREOWNED || carType === BOTH) {
                                bindNewNotification(store._id, PREOWNED);
                            }
                        } else {
                            bindNewNotification(store._id, area);
                        }
                    });
                });
            } else if (authorize(user?.accessLevel, USER_LEVEL_ACCESS)) {
                bindNewNotificationUser(user._id);
            }
        }
    }, [user, isSupported, permission, history, updateList]);

    const handleOpen = () => setOpen(true);

    const handleClose = () => setOpen(false);

    return (
        <div {...rest}>
            <IconButton
                style={{ color: 'white' }}
                ref={ref}
                onClick={handleOpen}
            >
                <BellIcon />
            </IconButton>
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                classes={{ paper: classes.popover }}
                anchorEl={ref.current}
                onClose={handleClose}
                open={isOpen}
            >
                <Box p={2}>
                    <Typography variant="h5" color="textPrimary">
                        {t('Notifications.Notifications')}
                    </Typography>
                </Box>
                {notifications && notifications.length === 0 ? (
                    <>
                        <Box p={2}>
                            <Typography variant="h6" color="textPrimary">
                                {t('Notifications.NoNotifications')}
                            </Typography>
                        </Box>
                    </>
                ) : (
                    <>
                        <List disablePadding>
                            {notifications &&
                                notifications.map((notification) => {
                                    return (
                                        <ListItem
                                            divider
                                            key={notification._id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                updateNotification(
                                                    notification._id
                                                );
                                                const url =
                                                    obtainUrlByNotificationType(
                                                        {
                                                            type: notification.type,
                                                            lead: notification?.lead,
                                                            conversation:
                                                                notification?.conversation,
                                                        }
                                                    );
                                                history.push(url);
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar
                                                    className={classes.icon}
                                                >
                                                    <SvgIcon fontSize="small">
                                                        <MessageIcon />
                                                    </SvgIcon>
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={t(
                                                    notification.message
                                                )}
                                                primaryTypographyProps={{
                                                    variant: 'subtitle2',
                                                    color: 'textPrimary',
                                                }}
                                                secondary={moment(
                                                    notification.createdAt
                                                ).fromNow()}
                                            />
                                        </ListItem>
                                    );
                                })}
                        </List>
                        <Box p={1} display="flex" justifyContent="center">
                            <Button
                                component={RouterLink}
                                size="small"
                                to="#"
                                onClick={handleMarkAllAsRead}
                            >
                                {t('Notifications.AllAsRead')}
                            </Button>
                        </Box>
                    </>
                )}
            </Popover>
        </div>
    );
};

export default Notifications;
